import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot
} from '@mui/lab';
import { TIMELINE_TYPES } from '../../../constants/timelineTypes';
import TimelineEventLabel from './TimelineEventLabel';
import TimelineUnitGroup from './TimelineUnitGroup';
import GoogleFontsIcon from '@shared/components/icons/GoogleFontsIcon';
import { ICON_TYPES } from '@shared/constants/icon-types';
import cx from 'classnames';

const MIN_DISTANCE = 48;
const DE_ENROLLED_TEXT = 'De-Enrollment Date';
const ENROLLED_TEXT = 'Enrollment Date';

/**
 * @typedef {object} TimelineChartTimelineItems
 * @property {Date} [timelineDate]
 * @property {number} [type]
 * @property {Array<*>} [orgUnits]
 * @property {number} [spacing]
 */

/**
 * @typedef {object} TimelineChartProps
 * @property {TimelineChartTimelineItems[]} [timelineItems]
 */

/**
 * @param {TimelineChartProps} props
 */
const TimelineChart = props => {
  const { timelineItems = [] } = props;
  return (
    <Timeline data-testid='timeline-chart' position='alternate'>
      {timelineItems.map((timelineItem, index) => {
        const { spacing, timelineDate, orgUnits, type } = timelineItem;
        const spacingHeight = spacing * MIN_DISTANCE;
        const isDeEnrolled = type === TIMELINE_TYPES.DE_ENROLLED;
        const label = isDeEnrolled ? DE_ENROLLED_TEXT : ENROLLED_TEXT;

        return (
          <TimelineItem key={index} data-testid={`timeline-item-${index}`}>
            <TimelineOppositeContent>
              <TimelineEventLabel date={timelineDate} label={label} />
            </TimelineOppositeContent>

            <TimelineSeparator>
              {index === 0 && (
                <TimelineConnector
                  data-testid='timeline-initial-connector'
                  className='timeline-initial-connector'
                />
              )}
              <TimelineDot
                className={cx(
                  isDeEnrolled ? 'timeline-unit-icon-deEnrolled' : 'timeline-unit-icon-enrolled'
                )}>
                <GoogleFontsIcon
                  dataTestId={`timeline-unit-icon-${index}`}
                  className='material-icons'
                  iconType={isDeEnrolled ? ICON_TYPES.DomainDisabled : ICON_TYPES.DomainAdd}
                />
              </TimelineDot>
              {hasNextItem(timelineItems, index) && (
                <TimelineConnector
                  data-testid={`timeline-connector-${index}`}
                  style={{ height: `${spacingHeight}px` }}
                />
              )}
            </TimelineSeparator>

            <TimelineContent>
              {orgUnits.map((group, groupIndex) => (
                <TimelineUnitGroup
                  key={groupIndex}
                  years={group.participationYears}
                  units={group.units}
                />
              ))}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

const hasNextItem = (timelineItems, index) => index < timelineItems.length - 1;

export default TimelineChart;
